import { chartToolbarButtonStyle } from './chartToolbarButtonStyle';
import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IoCloseOutline, IoInformationCircleOutline } from 'react-icons/io5';
import './ChartGuideButton.css';
import { ChartReferenceGuide } from './ChartReferenceGuide';
import { isRunningProfile } from '../../services/runningProfile';

/** Native dialog keeps keyboard focus inside and restores it to the guide trigger. */
export function ChartGuideButton({ type, title, paragraphs, setting, data }: {
  type: string;
  title: string;
  paragraphs?: string[];
  setting?: Record<string, unknown>;
  data?: unknown;
}) {
  const { t, i18n } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const key = i18n.exists(`chartGuide.content.${type}`) ? type : 'general';
  const sections = ['read', 'use', 'note'] as const;
  const runningProfile = type === 'running_profile' && isRunningProfile(data) ? data : null;
  const runningReferences = runningProfile ? [
    { label: t('charts.runningProfile.views.world'), version: runningProfile.world_benchmark?.version },
    { label: t('charts.runningProfile.views.benchmark'), version: runningProfile.benchmark.version },
  ].filter((reference): reference is { label: string; version: string } => Boolean(reference.version)) : [];
  return (
    <>
      <button type="button" className="chart-guide-button chart-toolbar-btn" style={chartToolbarButtonStyle} aria-haspopup="dialog" aria-label={t('chartGuide.open')} title={t('chartGuide.open')}
        onClick={() => dialog.current?.showModal()}>
        <IoInformationCircleOutline aria-hidden="true" />
      </button>
      <dialog ref={dialog} className="chart-guide-dialog" aria-labelledby={titleId} dir={i18n.dir()}
        onClick={(event) => { if (event.target === event.currentTarget) dialog.current?.close(); }}>
        <div className="chart-guide-dialog__content">
          <header>
            <h2 id={titleId}>{t('chartGuide.open')} · {title}</h2>
            <button type="button" autoFocus onClick={() => dialog.current?.close()} aria-label={t('chartGuide.close')}><IoCloseOutline aria-hidden="true" /></button>
          </header>
          {paragraphs ? paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)
            : sections.map((section) => (
              <section key={section}>
                <h3>{t(`chartGuide.${section}`)}</h3>
                <p>{t(`chartGuide.content.${key}.${section}`)}</p>
              </section>
            ))}
          {type === 'running_profile' && <section className="chart-guide-dialog__running-notes">
            <p>{t('charts.runningProfile.observedNotice')}</p>
            {runningReferences.length > 0 && <>
              <h3>{t('charts.runningProfile.benchmarkVersion')}</h3>
              {runningReferences.map(({ label, version }) => <p key={label}>{label} · <bdi dir="ltr">{version}</bdi></p>)}
            </>}
          </section>}
          <ChartReferenceGuide type={type} setting={setting} />
        </div>
      </dialog>
    </>
  );
}
